import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function CountUp({
  target,
  suffix = "",
  duration = 2800,
}: {
  target: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    setGlowing(true);
    let startTime: number | null = null;

    const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
    const compositeEase = (t: number) => {
      if (t < 0.15) {
        return 0.5 * (1 - Math.cos(Math.PI * (t / 0.15))) * 0.18;
      } else if (t < 0.75) {
        const p = (t - 0.15) / 0.6;
        return 0.18 + p * p * 0.62;
      } else {
        const p = (t - 0.75) / 0.25;
        return 0.8 + easeOutQuart(p) * 0.2;
      }
    };

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = compositeEase(progress);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(target);
        setTimeout(() => setGlowing(false), 400);
      }
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return (
    <div
      ref={ref}
      className="tabular-nums transition-all duration-300"
      style={{
        textShadow: glowing
          ? "0 0 24px rgba(201,168,76,0.7), 0 2px 10px rgba(0,0,0,0.7)"
          : "0 2px 10px rgba(0,0,0,0.7)",
        filter: glowing ? "brightness(1.18)" : "brightness(1)",
        transition: "text-shadow 0.3s ease, filter 0.3s ease",
      }}
    >
      {count}
      {suffix}
    </div>
  );
}

export default function AboutSection() {
  // 用 sectionRef 统一监听，手机和桌面都能触发
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="relative overflow-hidden py-32 px-6">
      {/* 背景图 */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/edited_8346cc8d04d6f7c0e2f969428af75530_55c34ecc.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      {/* 深色遮罩 */}
      <div className="absolute inset-0 z-0 bg-black/40 md:bg-black/40" style={{ background: 'rgba(15, 45, 30, 0.62)' }} />

      {/* 顶部渐变过渡 */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '160px',
          background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
        }}
      />
      {/* 底部渐变过渡 */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '160px',
          background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
        }}
      />

      <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <div
            className="lg:col-span-5 transition-all duration-1000 ease-out"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(40px)',
              transitionDelay: '0.05s',
            }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-champagne-gold/40 rounded-sm mb-6">
              <span
                className="font-cormorant text-xs tracking-widest"
                style={{
                  color: "#c9a84c",
                  textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                }}
              >
                ABOUT
              </span>
            </div>

            {/* 手机端统计数字 — 在 Rising Star 之前 */}
            <div
              className="flex lg:hidden items-center justify-start gap-10 mb-8 transition-all duration-700 ease-out"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(-24px)",
                transitionDelay: "0.08s",
              }}
            >
              <div>
                <div
                  className="text-4xl font-playfair font-bold mb-1"
                  style={{
                    color: "#c9a84c",
                    textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                  }}
                >
                  <CountUp target={150} suffix="+" duration={1800} />
                </div>
                <div
                  className="text-xs font-cormorant tracking-wide"
                  style={{
                    color: "#ede8d8",
                    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                    opacity: 0.75,
                  }}
                >
                  Performances
                </div>
              </div>
              <div>
                <div
                  className="text-4xl font-playfair font-bold mb-1"
                  style={{
                    color: "#c9a84c",
                    textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                  }}
                >
                  <CountUp target={10} suffix="+" duration={1200} />
                </div>
                <div
                  className="text-xs font-cormorant tracking-wide"
                  style={{
                    color: "#ede8d8",
                    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                    opacity: 0.75,
                  }}
                >
                  Full production Opera Roles
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-cinzel-decorative font-bold mb-4 leading-tight">
              <span
                className="block"
                style={{
                  color: "#c9a84c",
                  textShadow: "0 2px 8px rgba(0,0,0,0.7)",
                }}
              >
                Rising Star in the Opera World –
              </span>
              <span
                className="block text-2xl md:text-3xl font-bold font-cinzel-decorative tracking-wide mt-2"
                style={{
                  color: "#c9a84c",
                  textShadow: "0 1px 6px rgba(0,0,0,0.6)",
                }}
              >
                Climbing to New Heights with Determination
              </span>
            </h2>

            {/* 手机版图片 */}
            <div
              className="block lg:hidden my-6 transition-all duration-1000 ease-out"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0) scale(1)' : 'translateX(40px) scale(0.97)',
                transitionDelay: '0.3s',
              }}
            >
              <Link to="/gallery" className="block mx-auto cursor-pointer" style={{ maxWidth: "75%" }}>
                <div className="relative">
                  {/* 棕黄金属色细边框 */}
                  <div
                    className="absolute -inset-[1.5px] rounded-sm"
                    style={{
                      background: "linear-gradient(135deg, #8B6914 0%, #C8A84B 30%, #E8C96A 55%, #A07828 75%, #7A5510 100%)",
                      boxShadow: "0 0 14px 3px rgba(160,120,40,0.35), 0 0 30px 6px rgba(160,120,40,0.12)",
                    }}
                  />
                  <div className="relative overflow-hidden shadow-2xl">
                    <img
                      src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/b789d9095655427b6a900d4a2ef5fb62.jpeg"
                      alt="Wei Chen - Classical Baritone Opera Singer"
                      className="w-full h-auto object-cover object-top block"
                    />
                  </div>
                </div>
              </Link>
            </div>

            {/* 简介文字 */}
            <div
              className="space-y-4 font-cormorant text-lg md:text-lg leading-relaxed"
              style={{
                color: "#ede8d8",
                textShadow: "0 2px 8px rgba(0,0,0,0.7), 0 1px 4px rgba(0,0,0,0.5)",
              }}
            >
              <p>
                Wei Chen 陈 威, Baritone, graduated in 2025 from the Professional
                Studies Diploma program with a full scholarship at the{" "}
                <a
                  href="https://www.newschool.edu/mannes/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="border-b border-current transition-opacity duration-200 hover:opacity-70 cursor-pointer"
                  style={{ color: "#c9a84c" }}
                >
                  Mannes School of Music
                </a>
                , where he studied under Prof. William Burden.
                He holds a Master of Music degree from the{" "}
                <a
                  href="https://www.msmnyc.edu/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="border-b border-current transition-opacity duration-200 hover:opacity-70 cursor-pointer"
                  style={{ color: "#c9a84c" }}
                >
                  Manhattan School of Music
                </a>
                , where he studied under Maitland Peters, Chair of the Voice Department,
                and a Bachelor of Music degree from the{" "}
                <a
                  href="https://www.xhcom.edu.cn/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="border-b border-current transition-opacity duration-200 hover:opacity-70 cursor-pointer"
                  style={{ color: "#c9a84c" }}
                >
                  Xinghai Conservatory of Music
                </a>{" "}
                in Guangzhou, China, where he studied under Prof. Feng Guodong.
                He is currently based in NYC, under the management of{" "}
                <a
                  href="https://www.vpartistsmanagement.com/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="border-b border-current transition-opacity duration-200 hover:opacity-70 cursor-pointer"
                  style={{ color: "#c9a84c" }}
                >
                  VP Artists Management
                </a>{" "}
                and{" "}
                <a
                  href="https://www.3owlsartists.com/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="border-b border-current transition-opacity duration-200 hover:opacity-70 cursor-pointer"
                  style={{ color: "#c9a84c" }}
                >
                  Three Owls Artists Management and Press
                </a>
                .
              </p>
              <p>
                Wei has received many accolades, including the Gold Prize at the
                China Vocal International Competition in Singapore, the
                Excellence Award at the national-level China Golden Bell Music
                Award, the Gold Prize at the UNESCO International Choral Alliance
                Competition in Macau, and a semi-finalist placement in the
                Premiere Opera Competition.
              </p>
              <p>
                His recent roles include Abbot Cuno (Hildegard, Beth Morrison
                Project), Don Giovanni (Mannes Opera), Count Almaviva (Le Nozze
                di Figaro, Trentino Festival, Italy), Kinesias (Lysistrata,
                Mannes Opera), Escamillo (Carmen, Miami Music Festival), and
                Peter Quince (A Midsummer Night&apos;s Dream, Miami Music
                Festival), among others. He has also served as a judge for
                NYU&apos;s Voice of NYC Vocal Competition and has extensive
                performance experience as a lead singer in pop and rock music,
                as well as in musical theatre.
              </p>
              <p>
                Beyond the stage, Wei Chen is passionate about sharing the vocal arts in many forms. Through masterclasses and lectures, he enjoys inspiring and mentoring fellow young singers.
              </p>
            </div>

            <a
              href="/resume"
              className="inline-flex items-center gap-2 mt-8 transition-colors duration-300 font-cormorant text-lg md:text-xl group cursor-pointer whitespace-nowrap"
              style={{
                color: "#b8943a",
                textShadow: "0 2px 6px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.5)",
              }}
            >
              <span className="border-b border-current">View Full Résumé</span>
              <i 
                className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"
                style={{
                  animation: "resumeArrowBounce 1.2s ease-in-out infinite"
                }}
              ></i>
              <style jsx>{`
                @keyframes resumeArrowBounce {
                  0%, 100% { transform: translateX(0); }
                  50% { transform: translateX(10px); }
                }
              `}</style>
            </a>
          </div>

          {/* Right Content - 桌面版图片 */}
          <div className="hidden lg:block lg:col-span-7">
            {/* 统计数字 */}
            <div
              className="flex items-center justify-end gap-8 md:gap-16 mb-12 transition-all duration-700 ease-out"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(-32px)",
                transitionDelay: "0.1s",
              }}
            >
              <div className="text-right">
                <div
                  className="text-4xl md:text-6xl font-playfair font-bold mb-2"
                  style={{
                    color: "#c9a84c",
                    textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                  }}
                >
                  <CountUp target={150} suffix="+" duration={1800} />
                </div>
                <div
                  className="text-sm font-cormorant tracking-wide"
                  style={{
                    color: "#ede8d8",
                    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                    opacity: 0.7,
                  }}
                >
                  Performances
                </div>
              </div>
              <div className="text-right">
                <div
                  className="text-4xl md:text-6xl font-playfair font-bold mb-2"
                  style={{
                    color: "#c9a84c",
                    textShadow: "0 2px 10px rgba(0,0,0,0.7)",
                  }}
                >
                  <CountUp target={10} suffix="+" duration={1200} />
                </div>
                <div
                  className="text-sm font-cormorant tracking-wide"
                  style={{
                    color: "#ede8d8",
                    textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                    opacity: 0.7,
                  }}
                >
                  Full production Opera Roles
                </div>
              </div>
            </div>

            {/* 桌面版照片 — 可点击跳转 Gallery */}
            <div
              className="transition-all duration-1000 ease-out"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible
                  ? "translateX(0) translateY(0) rotate(0deg)"
                  : "translateX(60px) translateY(24px) rotate(2deg)",
                transitionDelay: "0.25s",
              }}
            >
              <Link to="/gallery" className="block mx-auto cursor-pointer" style={{ maxWidth: "58%" }}>
                <div className="relative">
                  {/* 棕黄金属色细边框 */}
                  <div
                    className="absolute -inset-[1.5px] rounded-sm"
                    style={{
                      background: "linear-gradient(135deg, #8B6914 0%, #C8A84B 30%, #E8C96A 55%, #A07828 75%, #7A5510 100%)",
                      boxShadow: "0 0 18px 4px rgba(160,120,40,0.35), 0 0 40px 10px rgba(160,120,40,0.12)",
                    }}
                  />
                  <div className="relative overflow-hidden shadow-2xl group">
                    <img
                      src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/b789d9095655427b6a900d4a2ef5fb62.jpeg"
                      alt="Wei Chen - Classical Baritone Opera Singer"
                      className="w-full h-auto object-cover object-top transition-transform duration-700 ease-in-out group-hover:scale-105 block"
                    />
                    <div
                      className="absolute bottom-0 left-0 right-0 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(160,120,40,0.22), transparent)",
                      }}
                    ></div>
                    {/* 悬停提示 */}
                    <div className="absolute inset-0 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                      <span
                        className="font-cormorant text-sm tracking-widest px-4 py-1.5 rounded-sm"
                        style={{
                          color: "#c9a84c",
                          background: "rgba(0,0,0,0.55)",
                          border: "1px solid rgba(201,168,76,0.35)",
                          textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                      >
                        VIEW PHOTO GALLERY
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
